import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { WhatsAppCtaBanner } from "../components/WhatsAppCtaBanner";
import { SectionEyebrow } from "../components/SectionEyebrow";
import { useSeo, SITE_URL } from "../hooks/useSeo";
import { useJsonLd } from "../hooks/useJsonLd";
import { buildVideoLd } from "../lib/seo";

const CLINIC_IMG = "/clinic-reception.jpg";
const CLINIC_IMG_2 = "/kosmedico-reception.jpg";
const DOCTOR_IMG = "/dr-hirschhausen.jpg";

const AboutPage = () => {
  const { t } = useTranslation();
  const process = t("about.process", { returnObjects: true });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useSeo({
    title: t("seo.about_title"),
    description: t("seo.about_description"),
    canonicalPath: "/sobre",
    image: `${SITE_URL}${CLINIC_IMG}`,
  });

  const videoLd = useMemo(
    () =>
      buildVideoLd({
        name: `${t("about.clinic_video_caption", "Thurgauerstrasse 117, Zürich")} · KosMedico Zürich`,
        description: `${t("about.clinic_video_label", "Em movimento")} · KosMedico Zürich`,
        poster: "/videos/v1-poster.jpg",
        src: "/videos/v1.mp4",
        uploadDate: "2026-06-10",
        durationSeconds: 31,
      }),
    [t]
  );
  useJsonLd(videoLd, "about-video");

  return (
    <div className="bg-[#F6F5F2]">
      <section className="pt-36 lg:pt-44 pb-20 lg:pb-28 relative overflow-hidden">
        <div className="absolute top-32 -left-20 w-96 h-96 rounded-full bg-[#A7CA99]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <SectionEyebrow num="01">{t("about.eyebrow")}</SectionEyebrow>
            <h1 className="font-serif text-5xl lg:text-7xl font-light tracking-tight leading-[0.95] text-[#3E5239]">
              {t("about.title_l1")}
              <br />
              <span className="italic text-[#A7CA99]">{t("about.title_l2")}</span>
            </h1>
            <div className="mt-10 space-y-6 max-w-xl text-[#6B7A66] font-light leading-relaxed">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>{t("about.p3")}</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#EFEDE6]">
              <img src={CLINIC_IMG} alt="Kosmedico Clinic Zürich" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#EFEDE6] border-y border-[#E4E2DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionEyebrow num="02">{t("about.process_title")}</SectionEyebrow>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E2DA] mt-10">
            {process.map((step, i) => (
              <div key={step.title} data-testid={`process-step-${i}`} className="bg-[#F6F5F2] p-8 lg:p-10">
                <div className="font-serif text-5xl font-light text-[#A7CA99]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-6 font-serif text-xl lg:text-2xl font-normal text-[#3E5239]">{step.title}</h3>
                <p className="mt-3 text-sm font-light text-[#6B7A66] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F6F5F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden bg-[#3E5239]">
              <img
                src={DOCTOR_IMG}
                alt="Dr. Karl Ernst Von Hirschhausen — KosMedico Zürich"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#1F2D1D]/75 to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-5 right-5 text-[#F6F5F2]">
                <div className="overline text-[#A7CA99]/90">{t("about.doctor_title")}</div>
                <div className="font-serif text-base lg:text-lg font-light mt-1 leading-tight">
                  {t("about.doctor_name")}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-4 text-[#A7CA99]">{t("about.doctor_title")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#3E5239] leading-tight">
              {t("about.doctor_name")}
            </h2>
            <p className="mt-8 text-[#6B7A66] font-light leading-relaxed max-w-xl">
              {t("about.doctor_bio")}
            </p>
          </div>
        </div>
      </section>

      {/* Clinic gallery — atmosphere */}
      <section className="py-20 lg:py-28 bg-[#EFEDE6] border-t border-[#E4E2DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionEyebrow num="04">{t("about.clinic_eyebrow", "A clínica")}</SectionEyebrow>
          <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-light tracking-tight leading-[1.1] text-[#3E5239] max-w-2xl">
            {t("about.clinic_title_l1", "Um espaço discreto,")}
            <br />
            <span className="italic text-[#A7CA99]">{t("about.clinic_title_l2", "no coração de Zürich.")}</span>
          </h2>
          <div className="grid lg:grid-cols-2 gap-px mt-12 bg-[#E4E2DA]">
            <div className="aspect-[4/3] lg:aspect-auto lg:row-span-2 bg-[#EFEDE6]">
              <img src={CLINIC_IMG_2} alt="Recepção KosMedico · Thurgauerstrasse 117, Zürich" loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/3] bg-[#EFEDE6]">
              <img src={CLINIC_IMG} alt="Ambiente da clínica · KosMedico Zürich" loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/3] bg-[#3E5239] relative overflow-hidden">
              <video
                src="/videos/v1.mp4"
                poster="/videos/v1-poster.jpg"
                playsInline
                muted
                loop
                autoPlay
                preload="metadata"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#1F2D1D]/70 to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-[#F6F5F2]">
                <div className="overline text-[#A7CA99]/90 mb-1">{t("about.clinic_video_label", "Em movimento")}</div>
                <div className="font-serif text-base lg:text-lg font-light leading-tight">
                  {t("about.clinic_video_caption", "Thurgauerstrasse 117, Zürich")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhatsAppCtaBanner source="about_page_bottom" />
    </div>
  );
};

export default AboutPage;
